import React, { FC, useMemo, useCallback, SetStateAction } from 'react';
import { Chip } from '@mui/material';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import Iconify from '@/components/media/iconify';
import {
  getOrderStatusColor,
  getContactStatusColor,
  getOrganizationStatusColor,
  getSubscriptionStatusColor,
} from '@/utils/formatters/status-colors';
import { FilterField } from '../types';

type FilterFieldValueProps = {
  field: FilterField;
  filters: Record<string, any>;
  onRemove: (value: SetStateAction<any>) => void;
};

const formatDateOrString = (date: any, format: string = 'L'): string => {
  if (!date) return '-';

  const parsedDate = moment.isMoment(date) ? date : moment(date, moment.ISO_8601, true);
  return parsedDate.isValid() ? parsedDate.tz('UTC').format(format) : String(date?.value || '-');
};

// Helper function to get status color based on context
const getStatusColorForField = (fieldName: string, statusId: string) => {
  if (fieldName !== 'status') return undefined;

  // Try to determine context from URL or other means
  const currentPath = window.location.pathname;

  if (currentPath.includes('/orders')) {
    return getOrderStatusColor(statusId);
  }
  if (currentPath.includes('/contacts')) {
    return getContactStatusColor(statusId);
  }
  if (currentPath.includes('/organizations')) {
    return getOrganizationStatusColor(statusId);
  }
  if (currentPath.includes('/subscriptions')) {
    return getSubscriptionStatusColor(statusId);
  }

  // Default fallback
  return undefined;
};

export const FilterFieldValue: FC<FilterFieldValueProps> = ({ field, onRemove, filters }) => {
  const { t } = useTranslation();

  const values = useMemo(() => filters[field.name], [filters, field.name]);

  const isAutocomplete = field.type === 'autocomplete';
  const autocompleteDisableClearable = isAutocomplete ? field.disableClearable : undefined;

  const disableClearable = useMemo(() => {
    return isAutocomplete ? (autocompleteDisableClearable ?? false) : false;
  }, [isAutocomplete, autocompleteDisableClearable]);

  const isRequired = useMemo(() => field.required ?? false, [field.required]);

  const handleRemove = useCallback(
    (id?: string) => {
      const newFilters = { ...filters };

      if (field.type === 'checkbox') {
        newFilters[field.name] = false;
      } else if (Array.isArray(values) && id) {
        newFilters[field.name] = values.filter((item: any) => (item._id ?? item.id) !== id);
      } else {
        delete newFilters[field.name];
      }

      [`${field.name}From`, `${field.name}Until`].forEach((key) => delete newFilters[key]);

      onRemove(newFilters);
    },
    [filters, values, field.name, onRemove]
  );

  const arrayValuesChips = useMemo(() => {
    if (!Array.isArray(values)) return null;

    return values.map((value: any) => {
      const id = value._id ?? value.id;
      const label = value.label ?? value.name ?? id;
      const showFlag =
        field.type === 'autocomplete' &&
        'showCountryFlag' in field &&
        field.showCountryFlag &&
        value.flag_image;

      const statusColor = getStatusColorForField(field.name, id);

      return (
        <Chip
          key={id}
          label={label}
          size="small"
          variant="soft"
          color={statusColor}
          onDelete={!disableClearable && !isRequired ? () => handleRemove(id) : undefined}
          icon={
            showFlag ? (
              <Iconify icon={`flagpack:${value.flag_image.toLowerCase()}`} width={16} />
            ) : undefined
          }
        />
      );
    });
  }, [values, field, disableClearable, isRequired, handleRemove]);

  const dateRangeChip = useMemo(() => {
    if (!values?.from && !values?.until) return null;

    const formatter = field.type === 'month-range' ? 'MMM YYYY' : 'L';
    const from = formatDateOrString(values.from, formatter);
    const until = formatDateOrString(values.until, formatter);

    return (
      <Chip
        label={`${t('From')} ${from} ${t('to')} ${until}`}
        size="small"
        variant="soft"
        onDelete={!disableClearable && !isRequired ? () => handleRemove() : undefined}
      />
    );
  }, [values, field.type, t, disableClearable, isRequired, handleRemove]);

  const searchTextChip = useMemo(() => {
    if (
      field.type !== 'search-text' ||
      !values ||
      typeof values !== 'string' ||
      values.length === 0
    ) {
      return null;
    }

    return (
      <Chip
        label={values}
        size="small"
        variant="soft"
        onDelete={!disableClearable && !isRequired ? () => handleRemove() : undefined}
      />
    );
  }, [field.type, values, disableClearable, isRequired, handleRemove]);

  const checkboxChip = useMemo(() => {
    if (field.type !== 'checkbox' || values !== true) {
      return null;
    }

    return (
      <Chip
        label={field.label}
        size="small"
        variant="soft"
        onDelete={!disableClearable && !isRequired ? () => handleRemove() : undefined}
      />
    );
  }, [field.type, field.label, values, disableClearable, isRequired, handleRemove]);

  if (Array.isArray(values)) {
    return <>{arrayValuesChips}</>;
  }

  if (dateRangeChip) {
    return dateRangeChip;
  }

  if (searchTextChip) {
    return searchTextChip;
  }

  if (checkboxChip) {
    return checkboxChip;
  }

  return null;
};
